import {
  isLabelTypeReason,
  mapEdgeCategoryToCategory,
} from '@modules/common/utils';
import { useGetTaxonomyLabelsQuery } from '@modules/taxonomy/hooks';
import dayjs from 'dayjs';
import {
  LabelType,
  TaxonomySortField,
  TaxonomySortOrder,
} from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { useGetUser } from 'v2/core/services/user';

export const useFetchReasons = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const [reasons, setReasons] = useState<ReasonOrEntityLabelType[]>([]);
  const pageNumber = useRef(0);
  const searchFilterInput = useRef<string>();
  const hits = useRef(0);
  const nextPageNumber = useRef<number>();

  const [fetchTaxonomyLabelsQuery, { loading }] = useGetTaxonomyLabelsQuery({
    onCompleted: (data) => {
      if (!data.getTaxonomy) {
        return;
      }
      hits.current = data.getTaxonomy.hits;
      nextPageNumber.current = data.getTaxonomy.nextPage;
      const newReasonLabels = data.getTaxonomy.nodes.map((node) => node.label);

      const newReasons = newReasonLabels.map((reasonLabel) => {
        const mappedReason: ReasonOrEntityLabelType = {
          ...reasonLabel,
          createdAt: dayjs(reasonLabel.createdAt),
          type: FeedbackLabelType.Reason,
          category: isLabelTypeReason(reasonLabel)
            ? mapEdgeCategoryToCategory(reasonLabel.metadata?.category)
            : undefined,
        };
        return mappedReason;
      });

      setReasons((prevReasons) => [...prevReasons, ...newReasons]);
    },
  });

  const fetchReasons = useCallback(
    async ({ page, searchFilter }: { page: number; searchFilter?: string }) => {
      if (
        page === pageNumber.current &&
        searchFilter === searchFilterInput.current
      ) {
        return;
      }

      pageNumber.current = page;
      searchFilterInput.current = searchFilter || undefined;

      if (page === 1) {
        setReasons([]);
      }

      const resp = await fetchTaxonomyLabelsQuery({
        variables: {
          tenantID,
          labelType: LabelType.Reason,
          page,
          responseSize: 50,
          sortField: TaxonomySortField.FeedbackVolume,
          sortOrder: TaxonomySortOrder.Desc,
          searchFilter: searchFilter || undefined,
        },
      });
      return resp;
    },
    [fetchTaxonomyLabelsQuery, tenantID],
  );

  useEffect(() => {
    if (!reasons.length && pageNumber.current === 0) {
      fetchReasons({ page: 1 });
    }
  }, [fetchReasons, pageNumber, reasons.length]);

  return [
    fetchReasons,
    {
      reasons,
      pageNumber: pageNumber.current,
      nextPageNumber: nextPageNumber.current,
      hits: hits.current,
      loading,
    },
  ] as const;
};
